import {
  types as sdkTypes,
  createImageVariantConfig,
} from '../../../util/sdkLoader';
import { storableError } from '../../../util/errors';
import { addMarketplaceEntities } from '../../../ducks/marketplaceData.duck';
import * as log from '../../../util/log';
import { denormalisedResponseEntities } from '../../../util/data';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_SIMILAR_PRODUCTS_REQUEST =
  'app/SimilarProducts/FETCH_SIMILAR_PRODUCTS_REQUEST';
export const FETCH_SIMILAR_PRODUCTS_SUCCESS =
  'app/SimilarProducts/FETCH_SIMILAR_PRODUCTS_SUCCESS';
export const FETCH_SIMILAR_PRODUCTS_ERROR =
  'app/SimilarProducts/FETCH_SIMILAR_PRODUCTS_ERROR';
export const CLEAR_SIMILAR_PRODUCTS =
  'app/SimilarProducts/CLEAR_SIMILAR_PRODUCTS';

// ================ Reducer ================ //

const initialState = {
  similarProductsInProgress: false,
  similarProductsError: null,
  similarProducts: [],
  lastFetchedListingId: null,
};

export default function similarProductsReducer(
  state = initialState,
  action = {}
) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SIMILAR_PRODUCTS_REQUEST:
      return {
        ...state,
        similarProductsInProgress: true,
        similarProductsError: null,
      };
    case FETCH_SIMILAR_PRODUCTS_SUCCESS:
      return {
        ...state,
        similarProductsInProgress: false,
        similarProducts: payload.products,
        lastFetchedListingId: payload.listingId,
      };
    case FETCH_SIMILAR_PRODUCTS_ERROR:
      return {
        ...state,
        similarProductsInProgress: false,
        similarProductsError: payload,
      };
    case CLEAR_SIMILAR_PRODUCTS:
      return {
        ...state,
        similarProductsInProgress: false,
        similarProductsError: null,
        similarProducts: [],
        lastFetchedListingId: null,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchSimilarProductsRequest = () => ({
  type: FETCH_SIMILAR_PRODUCTS_REQUEST,
});

export const fetchSimilarProductsSuccess = (similarProducts, listingId) => ({
  type: FETCH_SIMILAR_PRODUCTS_SUCCESS,
  payload: { products: similarProducts, listingId },
});

export const fetchSimilarProductsError = error => ({
  type: FETCH_SIMILAR_PRODUCTS_ERROR,
  error: true,
  payload: error,
});

export const clearSimilarProducts = () => ({
  type: CLEAR_SIMILAR_PRODUCTS,
});

// ================ Thunks ================ //

/**
 * Fetch similar products based on the current listing
 *
 * @param {UUID} listingId - The ID of the current listing
 * @param {Object} config - Configuration including layout settings
 * @return {Function} - Thunk
 */

// Keep track of fetched products by listing ID
const productsCache = {};

// Clear the cache - can be called when needed
export const clearProductsCache = () => {
  Object.keys(productsCache).forEach(key => {
    delete productsCache[key];
  });
};

export const fetchSimilarProducts = (listingId, categoryLevel1, config) => (
  dispatch,
  getState,
  sdk
) => {
  // Convert listingId to string format
  const listingIdString = listingId?.uuid || listingId?.toString() || listingId;

  // Handle missing category - return empty array immediately
  if (!categoryLevel1) {
    // Clear any previous similar products and cache for this listing
    delete productsCache[listingIdString];
    dispatch(clearSimilarProducts());
    dispatch(fetchSimilarProductsSuccess([], listingIdString));
    return Promise.resolve([]);
  }

  // Check if we have cached results for this listing ID
  if (productsCache[listingIdString]) {
    dispatch(
      fetchSimilarProductsSuccess(
        productsCache[listingIdString],
        listingIdString
      )
    );
    return Promise.resolve(productsCache[listingIdString]);
  }

  // Setup image variants configuration
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Set up query parameters - fetch 24 items that we'll randomize later
  const params = {
    pub_categoryLevel1: categoryLevel1,
    perPage: 24,
    include: ['author', 'images', 'currentStock'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  dispatch(fetchSimilarProductsRequest());

  return sdk.listings
    .query(params)
    .then(response => {
      // Filter out the current listing and closed listings
      const listings = response.data.data.filter(
        listing =>
          listing.id.uuid !== listingIdString &&
          listing.attributes &&
          listing.attributes.state !== 'closed'
      );

      // Add marketplace entities to Redux store
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));

      // Get normalized listings, filter out current listing
      const allSimilarProducts = denormalisedResponseEntities(response).filter(
        listing => listing.id.uuid !== listingIdString
      );

      // Randomize and select 4 listings
      const shuffleArray = array => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      // Randomize and take first 4 (or fewer if not enough listings)
      const similarProducts = shuffleArray(allSimilarProducts).slice(0, 4);

      // Store in cache
      productsCache[listingIdString] = similarProducts;

      dispatch(fetchSimilarProductsSuccess(similarProducts, listingIdString));
      return similarProducts;
    })
    .catch(e => {
      dispatch(fetchSimilarProductsError(storableError(e)));
      return [];
    });
};
